import { useMutation, useQuery } from "@apollo/client";
import { Form, Formik, FormikProps } from "formik";
import { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Button from "src/components/Button";
import CustomDropdown from "src/components/CustomDropdown";
import Input from "src/components/Input";
import InputPhone from "src/components/InputPhone";
import { ADD_EVENT_TEAM_MEMBER } from "src/graphql/mutations/event.mutation";
import {
  EVENT_TEAM_ROLES,
  GET_EVENTS_FOR_DASHBOARD,
} from "src/graphql/queries/event.queries";
import useAppStore from "src/store/utils";
import {
  AddEventTeamMemberMutation,
  AddEventTeamMemberMutationVariables,
  EventOwnerRole,
  EventTeamRolesQuery,
  EventTeamRolesQueryVariables,
} from "src/types/api.d";
import * as Yup from "yup";
import {
  normalizeTeamPhone,
  validTeamPhone,
  weddingRoleOptions,
} from "../_teamRoles";

const addTeamMemberSchema = Yup.object().shape({
  firstName: Yup.string()
    .min(2, "Please fill in at least two characters")
    // .matches(
    //   /^\b(?!.*\.{2})[a-zA-Z.]+(?:\s[a-zA-Z.]+)\b$/,
    //   "e.g Firstname Lastname"
    // )
    .required("Please fill in your team member's first name"),
  lastName: Yup.string()
    .min(2, "Please fill in at least two characters")
    .required("Please fill in your team member's last name"),
  email: Yup.string().email("Please enter a correct email address"),
  role: Yup.string().required("Your role is required"),
});

interface AddTeamMemberProps {
  id: number | null;
  handleGoBack: () => void;
}

interface Values {
  firstName: string;
  lastName: string;
  email: string;
  role: EventOwnerRole | "";
}

const AddTeamMemberModal: React.FC<AddTeamMemberProps> = ({
  id,
  handleGoBack,
}) => {
  const [teamMemberName, setTeamMemberName] = useState<string>("");
  const [phoneNumberValue, setPhoneNumberValue] = useState<string>("");
  const navigate = useNavigate();
  const { event } = useAppStore();
  const rolesQuery = useQuery<
    EventTeamRolesQuery,
    EventTeamRolesQueryVariables
  >(EVENT_TEAM_ROLES, {
    variables: { eventId: event.uuid },
    skip: !event.uuid,
  });
  const roleOptions = weddingRoleOptions(rolesQuery.data?.ownerTypes || []);

  const [addTeamMemberFn, { loading: addTeamMemberLoading }] = useMutation<
    AddEventTeamMemberMutation,
    AddEventTeamMemberMutationVariables
  >(ADD_EVENT_TEAM_MEMBER, {
    onCompleted() {
      handleGoBack();
      toast.success(
        <p className="toast">
          Team member invite sent to {teamMemberName ?? "an owner"}.
        </p>
      );
    },
    onError(error) {
      if (error?.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "An error occured"}</p>
        );
      } else {
        navigate("/clear");
      }
    },
  });

  return (
    <div className="w-full rounded bg-white">
      <Formik
        enableReinitialize
        initialValues={{
          firstName: "",
          lastName: "",
          email: "",
          role: "",
        }}
        validationSchema={addTeamMemberSchema}
        onSubmit={(values, actions) => {
          setTeamMemberName(values.firstName);
          if (
            id &&
            values.role !== "" &&
            validTeamPhone(phoneNumberValue) &&
            roleOptions.some((option) => option.value === values.role)
          ) {
            addTeamMemberFn({
              variables: {
                team: {
                  event_id: id,
                  ...(values.email ? { email: values.email } : {}),
                  first_name: values.firstName,
                  last_name: values.lastName,
                  phone_number: normalizeTeamPhone(phoneNumberValue),
                  role: values.role,
                },
              },
              refetchQueries: [
                {
                  query: GET_EVENTS_FOR_DASHBOARD,
                  variables: {
                    isInvited: false,
                    page: 1,
                    pageSize: 200,
                  },
                },
              ],
            });
            actions.resetForm();
          }
        }}
      >
        {(props: FormikProps<Values>) => (
          <Form className="">
            <div className="flex flex-col gap-4">
              <Input
                type="text"
                name="firstName"
                label="First Name"
                placeholder="Timi"
              />
              <Input
                type="text"
                name="lastName"
                label="Last Name"
                placeholder="Idowu"
              />
              <Input
                type="text"
                name="email"
                label="Email (optional)"
                placeholder="timi1234@mail.com"
              />
              <InputPhone
                name="phoneNumber"
                label="WhatsApp Number"
                placeholder="Enter WhatsApp Number"
                isError={
                  !!phoneNumberValue && !validTeamPhone(phoneNumberValue)
                }
                value={phoneNumberValue}
                setValue={setPhoneNumberValue}
              />
              <div className="mb-4">
                <label htmlFor="role" className="text-sm">
                  Role
                </label>
                <CustomDropdown
                  options={roleOptions}
                  value={props.values.role}
                  onChange={(value) => props.setFieldValue("role", value)}
                  placeholder="Select Role"
                />
              </div>
            </div>
            {rolesQuery.error && (
              <p role="alert" className="my-3 text-sm text-red-600">
                {rolesQuery.error.message}{" "}
                <button
                  type="button"
                  className="underline"
                  onClick={() => void rolesQuery.refetch()}
                >
                  Retry
                </button>
              </p>
            )}
            <div className="flex justify-end">
              <Button
                type="submit"
                label="Add Member"
                wrapperBtnClassName="!w-max"
                loader={addTeamMemberLoading}
                disabled={
                  props.values.role === "" ||
                  rolesQuery.loading ||
                  !!rolesQuery.error ||
                  !validTeamPhone(phoneNumberValue) ||
                  props.values.firstName === "" ||
                  props.values.lastName === "" ||
                  phoneNumberValue === "" ||
                  !props.isValid ||
                  props.isSubmitting
                }
              >
                Add Member
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AddTeamMemberModal;
